import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Mic, ShieldCheck, Activity, Layers } from "lucide-react";
import SplineRobot from "./SplineRobot";
import JarvisVoicePanel from "./JarvisVoicePanel";
import useJarvisVoice from "../../lib/useJarvisVoice";

const TRUST = [
  { v: "8", l: "intelligence layers" },
  { v: "4", l: "content types" },
  { v: "8", l: "languages" },
  { v: "~12s", l: "per analysis" },
];

const FEATURE_PILLS = [
  { icon: ShieldCheck, label: "Truthfulness" },
  { icon: Activity, label: "Tone & Bias" },
  { icon: Layers, label: "Cross-source" },
  { icon: Mic, label: "Voice mode" },
];

// Simple breakpoint hook so we render the voice panel only once (no duplicate testids)
function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : true
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isDesktop;
}

export default function Hero() {
  const nav = useNavigate();
  const isDesktop = useIsDesktop();
  const [voiceError, setVoiceError] = useState("");

  // Jarvis-style intelligent voice assistant
  const {
    supported, phase, language, setLanguage,
    userCaption, aiCaption, isInterim, audioLevel, analyser,
    startListening, stopAll, introduce, voices, isIntroduced,
    modelUsed,
    selectedVoiceName, preferredVoiceName, setPreferredVoiceName
  } = useJarvisVoice({
    autoStart: true, // Auto-introduce on mount
    onError: (msg) => {
      console.error("[Jarvis] Error:", msg);
      setVoiceError(msg);
      setTimeout(() => setVoiceError(""), 5000); // Clear after 5 seconds
    }
  });

  // Handle robot/Spline click
  const handleVoiceTrigger = React.useCallback(() => {
    if (!isIntroduced && phase !== "introducing") {
      introduce();
    } else {
      startListening();
    }
  }, [isIntroduced, phase, introduce, startListening]);

  const robotGlow =
    phase === "listening" ? "info"
      : phase === "speaking" ? "success"
        : phase === "introducing" ? "brand"
          : phase === "thinking" ? "brand"
            : "brand";

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 hero-spotlight pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-8 pt-4 lg:pt-4 pb-16 lg:pb-12">
        {/* Eyebrow */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          {/* LEFT: Copy */}
          <div className="lg:col-span-5 relative z-10">
            {/* Acronym Eyebrow */}
            <div
              className="mb-6 inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase fade-up"
              style={{ animationDelay: "0s" }}
            >
              <span className="text-zinc-500 text-[11px] tracking-wider" style={{ wordSpacing: "0.45em" }}>
                <span className="text-brand/70 font-bold text-[13px]">N</span>eural{" "}
                <span className="text-brand/70 font-bold text-[13px]">E</span>ye for{" "}
                <span className="text-brand/70 font-bold text-[13px]">T</span>ruth{" "}
                <span className="text-brand/70 font-bold text-[13px]">R</span>eliability &amp;{" "}
                <span className="text-brand/70 font-bold text-[13px]">A</span>wareness
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="text-white tracking-tight leading-[1.02] fade-up"
              style={{ animationDelay: "0.05s" }}
            >
              <span className="block font-semibold text-5xl sm:text-6xl lg:text-[64px] xl:text-[76px]">
                Decode the narrative.
              </span>
              <span className="block font-serif italic font-light text-5xl sm:text-6xl lg:text-[64px] xl:text-[76px] mt-1">
                <span className="text-brand">Talk to the news.</span>
              </span>
            </h1>

            <p
              className="mt-7 max-w-xl text-zinc-400 text-lg leading-relaxed fade-up"
              style={{ animationDelay: "0.15s" }}
            >
              Paste an article, link, PDF, or image, or simply{" "}
              <span className="text-zinc-200">tap N.E.T.R.A and speak</span>. Eight quiet
              checks for truth, balance, sources, and tone. A verdict you can
              actually defend.
            </p>

            <div
              className="mt-7 flex flex-wrap items-center gap-3 fade-up"
              style={{ animationDelay: "0.25s" }}
            >
              <button
                onClick={() => nav("/app")}
                data-testid="hero-cta-analyze"
                className="group inline-flex items-center gap-2 bg-zinc-800/80 text-white hover:bg-zinc-700/80 px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-md shadow-black/20 hover:shadow-lg hover:shadow-black/30 backdrop-blur-sm border border-zinc-700/50"
              >
                Analyze Content
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={handleVoiceTrigger}
                data-testid="hero-cta-voice"
                className="inline-flex items-center gap-2 text-zinc-300 hover:text-white px-6 py-3 rounded-full transition-all hover:bg-zinc-800/40 border border-transparent hover:border-zinc-700/40"
              >
                <Mic size={15} className="text-signal-info" /> Ask N.E.T.R.A
              </button>
            </div>

            <div
              className="mt-7 flex flex-wrap gap-2 fade-up"
              style={{ animationDelay: "0.32s" }}
            >
              {FEATURE_PILLS.map((p) => {
                const Icon = p.icon;
                return (
                  <span
                    key={p.label}
                    className="inline-flex items-center gap-1.5 text-xs text-zinc-400 border border-ink-border-subtle bg-ink-surface/60 px-3 py-1.5 rounded-full"
                  >
                    <Icon size={12} className="text-brand" />
                    {p.label}
                  </span>
                );
              })}
            </div>
          </div>

          {/* RIGHT (DESKTOP): Big interactive robot, click anywhere to talk */}
          {isDesktop && (
            <div
              className="lg:col-span-7 relative min-h-[400px] h-[520px] xl:h-[600px] -mr-6 lg:-mr-10 xl:-mr-16"
              id="voice"
            >
              {/* Tap-hint floating badge top-right */}
              <div
                className={`absolute top-6 right-6 z-30 inline-flex items-center gap-2 text-xs bg-black/60 border backdrop-blur-md rounded-full px-3 py-1.5 pointer-events-none transition-colors ${phase === "listening" ? "text-signal-info border-signal-info/50"
                  : phase === "thinking" ? "text-brand border-brand/50"
                    : phase === "speaking" ? "text-signal-success border-signal-success/50"
                      : phase === "introducing" ? "text-brand border-brand/50"
                        : "text-zinc-300 border-ink-border"
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${phase === "listening" ? "bg-signal-info animate-pulse"
                  : phase === "thinking" ? "bg-brand animate-pulse"
                    : phase === "speaking" ? "bg-signal-success animate-pulse"
                      : phase === "introducing" ? "bg-brand animate-pulse"
                        : "bg-signal-success pulse-soft"
                  }`} />
                {phase === "idle" ? "Click N.E.T.R.A to activate" :
                  phase === "listening" ? "Listening…" :
                    phase === "thinking" ? "Processing…" :
                      phase === "introducing" ? "Initializing…" :
                        "Speaking…"}
              </div>

              {/* The 3D robot, whole canvas is the click target */}
              <SplineRobot
                className="absolute inset-0"
                testId="hero-spline"
                onClick={handleVoiceTrigger}
                glow={robotGlow}
                ariaLabel="Click to talk to N.E.T.R.A"
              />

              {/* Jarvis Voice panel, OVERLAPPING the robot's lower body */}
              <div
                className="absolute z-20 left-1/2 -translate-x-1/2 bottom-4 lg:bottom-8 xl:bottom-8 w-[min(460px,calc(100%-2rem))] fade-up"
                style={{ animationDelay: "0.6s", marginLeft: "-14rem" }}
              >
                <JarvisVoicePanel
                  supported={supported}
                  phase={phase}
                  language={language}
                  setLanguage={setLanguage}
                  userCaption={userCaption}
                  aiCaption={aiCaption}
                  isInterim={isInterim}
                  audioLevel={audioLevel}
                  analyser={analyser}
                  onMicClick={handleVoiceTrigger}
                  errorMsg={voiceError}
                  voices={voices}
                  modelUsed={modelUsed}
                  selectedVoiceName={selectedVoiceName}
                  preferredVoiceName={preferredVoiceName}
                  setPreferredVoiceName={setPreferredVoiceName}
                />
              </div>
            </div>
          )}

          {/* MOBILE: voice panel only (no robot) */}
          {!isDesktop && (
            <div className="lg:hidden" id="voice">
              <JarvisVoicePanel
                supported={supported}
                phase={phase}
                language={language}
                setLanguage={setLanguage}
                userCaption={userCaption}
                aiCaption={aiCaption}
                isInterim={isInterim}
                audioLevel={audioLevel}
                analyser={analyser}
                onMicClick={handleVoiceTrigger}
                compact
                errorMsg={voiceError}
                voices={voices}
                modelUsed={modelUsed}
                selectedVoiceName={selectedVoiceName}
                preferredVoiceName={preferredVoiceName}
                setPreferredVoiceName={setPreferredVoiceName}
              />
            </div>
          )}
        </div>

        {/* Trust strip */}
        <div
          className="mt-20 lg:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-y-6 max-w-3xl fade-up"
          style={{ animationDelay: "0.4s" }}
        >
          {TRUST.map((s, i) => (
            <div key={i} className="border-l border-ink-border-subtle pl-4">
              <div className="font-serif text-3xl text-white tabular-nums">{s.v}</div>
              <div className="mt-1 text-xs text-zinc-500">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
